import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Loader2, Users, TrendingUp, DollarSign, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchCalendarData } from "@/lib/api";
import { formatNumber, formatCurrency } from "@/lib/constants";
import { useTheme } from "@/contexts/ThemeContext";
import ThemeToggle from "@/components/ThemeToggle";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_NAMES_SHORT = ["S", "M", "T", "W", "T", "F", "S"];

function getDaysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month - 1, 1).getDay();
}

export default function CalendarView({ onEditShift, taxRate, retentionRate, wrvuRate = 25 }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Track if on mobile
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await fetchCalendarData(year, month);
      setShifts(data);
    } catch {
      setShifts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const handlePrev = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };

  const handleNext = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const handleDayClick = (dateStr) => {
    if (onEditShift) onEditShift(dateStr);
  };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const shiftMap = {};
  shifts.forEach(s => { shiftMap[s.date] = s; });

  const monthlyPatients = shifts.reduce((sum, s) => sum + (s.total_patients || 0), 0);
  const monthlyWrvus = shifts.reduce((sum, s) => sum + (s.total_wrvus || 0), 0);
  const monthlyHourlyPay = shifts.reduce((sum, s) => sum + (s.total_hourly_pay || 0), 0);
  const monthlyPreTax = monthlyWrvus * wrvuRate + monthlyHourlyPay;
  const monthlyPostTax = monthlyPreTax * retentionRate;

  return (
    <div data-testid="calendar-view" className="pb-32">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className={`text-3xl md:text-4xl font-bold tracking-tight mb-1 ${
            isDark ? 'text-slate-100' : 'text-gray-900'
          }`} style={{ fontFamily: "'Manrope', sans-serif" }}>
            Monthly Calendar
          </h1>
          <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>Click any day to edit that shift</p>
        </div>
        <ThemeToggle />
      </div>

      {/* Month navigation */}
      <div className="flex items-center justify-between mb-6">
        <Button data-testid="calendar-prev" variant="outline" onClick={handlePrev}
          className={isDark 
            ? "border-slate-700/50 hover:bg-slate-800 text-slate-300"
            : "border-gray-300 hover:bg-gray-100 text-gray-700"
          }>
          <ChevronLeft size={20} />
        </Button>
        <h2 data-testid="calendar-month-title" className={`text-2xl font-bold ${
          isDark ? 'text-slate-100' : 'text-gray-900'
        }`} style={{ fontFamily: "'Manrope', sans-serif" }}>
          {MONTH_NAMES[month - 1]} {year}
        </h2>
        <Button data-testid="calendar-next" variant="outline" onClick={handleNext}
          className={isDark 
            ? "border-slate-700/50 hover:bg-slate-800 text-slate-300"
            : "border-gray-300 hover:bg-gray-100 text-gray-700"
          }>
          <ChevronRight size={20} />
        </Button>
      </div>

      {/* Monthly totals */}
      <div data-testid="monthly-totals" className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className={`rounded-xl border p-4 ${
          isDark ? 'border-slate-700/30 bg-slate-800/30' : 'border-gray-200 bg-white'
        }`}>
          <div className="flex items-center gap-2 mb-1">
            <Users size={14} className="text-blue-400" />
            <span className={`text-xs uppercase tracking-widest font-semibold ${
              isDark ? 'text-slate-500' : 'text-gray-500'
            }`}>Patients</span>
          </div>
          <p data-testid="monthly-patients" className={`text-2xl font-mono font-bold ${
            isDark ? 'text-blue-400' : 'text-blue-600'
          }`}>{monthlyPatients}</p>
        </div>
        <div className={`rounded-xl border p-4 ${
          isDark ? 'border-slate-700/30 bg-slate-800/30' : 'border-gray-200 bg-white'
        }`}>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} className="text-emerald-400" />
            <span className={`text-xs uppercase tracking-widest font-semibold ${
              isDark ? 'text-slate-500' : 'text-gray-500'
            }`}>wRVUs</span>
          </div>
          <p data-testid="monthly-wrvus" className={`text-2xl font-mono font-bold ${
            isDark ? 'text-emerald-400' : 'text-emerald-600'
          }`}>{formatNumber(monthlyWrvus)}</p>
        </div>
        <div className={`rounded-xl border p-4 ${
          isDark ? 'border-slate-700/30 bg-slate-800/30' : 'border-gray-200 bg-white'
        }`}>
          <div className="flex items-center gap-2 mb-1">
            <DollarSign size={14} className={isDark ? "text-amber-400" : "text-amber-500"} />
            <span className={`text-xs uppercase tracking-widest font-semibold ${
              isDark ? 'text-slate-500' : 'text-gray-500'
            }`}>Pre-Tax $</span>
          </div>
          <p data-testid="monthly-pretax" className={`text-2xl font-mono font-bold ${
            isDark ? 'text-amber-400' : 'text-amber-600'
          }`}>{formatCurrency(monthlyPreTax)}</p>
        </div>
        <div className={`rounded-xl border p-4 ${
          isDark ? 'border-slate-700/30 bg-slate-800/30' : 'border-gray-200 bg-white'
        }`}>
          <div className="flex items-center gap-2 mb-1">
            <DollarSign size={14} className="text-sky-400" />
            <span className={`text-xs uppercase tracking-widest font-semibold ${
              isDark ? 'text-slate-500' : 'text-gray-500'
            }`}>Post-Tax $</span>
          </div>
          <p data-testid="monthly-posttax" className={`text-2xl font-mono font-bold ${
            isDark ? 'text-sky-400' : 'text-sky-600'
          }`}>{formatCurrency(monthlyPostTax)}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className={`h-6 w-6 animate-spin ${isDark ? 'text-slate-500' : 'text-gray-500'}`} />
        </div>
      ) : (
        <div className={`rounded-xl border overflow-hidden ${
          isDark ? 'border-slate-700/30 bg-slate-800/20' : 'border-gray-200 bg-white'
        }`}>
          {/* Day headers */}
          <div className={`grid grid-cols-7 border-b ${isDark ? 'border-slate-700/30' : 'border-gray-200'}`}>
            {(isMobile ? DAY_NAMES_SHORT : DAY_NAMES).map((day, idx) => (
              <div key={idx} className={`py-2 sm:py-3 text-center text-[10px] sm:text-xs uppercase tracking-widest font-semibold ${
                isDark ? 'text-slate-500' : 'text-gray-500'
              }`}>
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} className={`min-h-[60px] sm:min-h-[120px] border-b border-r ${
                isDark ? 'border-slate-700/15 bg-slate-900/20' : 'border-gray-100 bg-gray-50/50'
              }`} />
            ))}

            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const shift = shiftMap[dateStr];
              const isToday = day === now.getDate() && month === now.getMonth() + 1 && year === now.getFullYear();
              const dayPreTax = shift ? (shift.total_wrvus * wrvuRate + (shift.total_hourly_pay || 0)) : 0;
              const dayPostTax = dayPreTax * retentionRate;

              return (
                <div
                  key={day}
                  data-testid={`calendar-day-${day}`}
                  onClick={() => handleDayClick(dateStr)}
                  className={`min-h-[60px] sm:min-h-[120px] border-b border-r p-1 sm:p-2 cursor-pointer transition-colors group ${
                    isDark 
                      ? `border-slate-700/15 hover:bg-slate-700/20 ${shift ? 'bg-slate-800/40' : ''}`
                      : `border-gray-100 hover:bg-gray-100 ${shift ? 'bg-blue-50/50' : ''}`
                  } ${isToday ? 'ring-1 ring-inset ring-blue-500/40' : ''}`}
                >
                  {/* Mobile layout */}
                  <div className="sm:hidden">
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-semibold ${
                        isToday 
                          ? 'text-blue-400' 
                          : shift 
                            ? isDark ? 'text-emerald-400' : 'text-emerald-600'
                            : isDark ? 'text-slate-500' : 'text-gray-500'
                      }`}>{day}</span>
                      {shift && (
                        <div className={`w-2 h-2 rounded-full ${isDark ? 'bg-emerald-500' : 'bg-emerald-400'}`} />
                      )}
                    </div>
                    {shift && (
                      <div className="mt-0.5 space-y-0">
                        <div className={`text-[9px] font-mono font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                          {shift.total_patients}p
                        </div>
                        <div className={`text-[9px] font-mono font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                          {formatNumber(shift.total_wrvus || 0)}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Desktop layout */}
                  <div className="hidden sm:block">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-sm font-semibold ${
                          isToday 
                            ? 'text-blue-400' 
                            : isDark ? 'text-slate-400' : 'text-gray-700'
                        }`}>{day}</span>
                        {shift && shift.clinic_location && (
                          <span className={`text-[10px] truncate max-w-[80px] ${
                            isDark ? 'text-slate-500' : 'text-gray-500'
                          }`} title={shift.clinic_location}>
                            {shift.clinic_location}
                          </span>
                        )}
                      </div>
                      {shift && (
                        <Pencil size={10} className={`opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ${
                          isDark ? 'text-slate-600' : 'text-gray-400'
                        }`} />
                      )}
                    </div>

                    {shift && (
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1">
                          <Users size={10} className="text-blue-400 flex-shrink-0" />
                          <span className={`text-xs font-mono font-bold ${
                            isDark ? 'text-blue-400' : 'text-blue-600'
                          }`}>{shift.total_patients?.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <TrendingUp size={10} className="text-emerald-400 flex-shrink-0" />
                          <span className={`text-xs font-mono font-bold ${
                            isDark ? 'text-emerald-400' : 'text-emerald-600'
                          }`}>{formatNumber(shift.total_wrvus || 0)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <DollarSign size={10} className={isDark ? "text-amber-400" : "text-amber-500"} flex-shrink-0 />
                          <span className={`text-xs font-mono font-bold ${
                            isDark ? 'text-amber-400' : 'text-amber-600'
                          }`}>{formatCurrency(dayPreTax)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <DollarSign size={10} className="text-sky-400 flex-shrink-0" />
                          <span className={`text-xs font-mono font-bold ${
                            isDark ? 'text-sky-400' : 'text-sky-600'
                          }`}>{formatCurrency(dayPostTax)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Mobile shift list - shows below calendar on mobile for better readability */}
      {isMobile && !loading && shifts.length > 0 && (
        <div className="mt-4 sm:hidden">
          <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
            Shifts this month
          </h3>
          <div className="space-y-2">
            {shifts.map((shift) => {
              const dayPreTax = (shift.total_wrvus * wrvuRate + (shift.total_hourly_pay || 0));
              const dayPostTax = dayPreTax * retentionRate;
              const shiftDate = new Date(shift.date + 'T00:00:00');
              
              return (
                <div
                  key={shift.date}
                  onClick={() => handleDayClick(shift.date)}
                  className={`p-3 rounded-lg border cursor-pointer ${
                    isDark 
                      ? 'bg-slate-800/50 border-slate-700/30 active:bg-slate-700/50'
                      : 'bg-white border-gray-200 active:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${isDark ? 'text-slate-200' : 'text-gray-900'}`}>
                        {shiftDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                      {shift.clinic_location && (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {shift.clinic_location}
                        </span>
                      )}
                    </div>
                    <Pencil size={14} className={isDark ? 'text-slate-500' : 'text-gray-400'} />
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div>
                      <p className={`text-[10px] uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Patients</p>
                      <p className={`text-sm font-mono font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{shift.total_patients}</p>
                    </div>
                    <div>
                      <p className={`text-[10px] uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>wRVUs</p>
                      <p className={`text-sm font-mono font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{formatNumber(shift.total_wrvus || 0)}</p>
                    </div>
                    <div>
                      <p className={`text-[10px] uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Pre-Tax</p>
                      <p className={`text-sm font-mono font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{formatCurrency(dayPreTax)}</p>
                    </div>
                    <div>
                      <p className={`text-[10px] uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Post-Tax</p>
                      <p className={`text-sm font-mono font-bold ${isDark ? 'text-sky-400' : 'text-sky-600'}`}>{formatCurrency(dayPostTax)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
